import { useState, useEffect } from "react"
import Fuse from "fuse.js"
import GameImage from "../GameImage"
import { useGlobalContext } from "../../data/GlobalContext"
import { useLocation } from "react-router-dom"
import LoginToPlay from "../../data/LoginToPlay"

const SearchGamesLibrary = ({ onClose }) => {
  const { allGamesFlat, tokenUrl, launchUrl, username } = useGlobalContext()
  const location = useLocation()

  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const [searchQuery, setSearchQuery] = useState("")
  const [searchResultsLimit, setSearchResultsLimit] = useState(8)
  const [login, setLogin] = useState(false)

  const gamesOffline = ["playson"]
  const games = allGamesFlat || []

  // Exclude games from offline providers.
  const filteredGamesList = games.filter(
    (game) =>
      game.provider && !gamesOffline.includes(game.provider.toLowerCase())
  )

  const updateRecentGames = (gameId) => {
    const stored = JSON.parse(localStorage.getItem("recentGames")) || []
    const filtered = stored.filter((id) => id !== gameId)
    filtered.unshift(gameId)
    const newRecent = filtered.slice(0, 5)
    localStorage.setItem("recentGames", JSON.stringify(newRecent))
  }

  // Fuse search configuration.
  const fuse = new Fuse(filteredGamesList, {
    keys: [
      "gameName",
      { name: "provider", getFn: (game) => game.provider || "Unknown" },
    ],
    threshold: 0.4,
    ignoreLocation: true,
  })

  const filteredGames = searchQuery.trim()
    ? fuse.search(searchQuery).map((result) => result.item)
    : []

  // Function to launch game.
  const launchGameInternal = async (gameId) => {
    try {
      if (!username) {
        setLogin(true)
        return
      }
      const requestUrl = `${tokenUrl}?u=${username}&g=${gameId}&t=transfer&m=mobile`
      const response = await fetch(requestUrl)
      if (!response.ok) {
        alert("Failed to fetch game token. Please try again.")
        return
      }
      const data = await response.json()
      const sessionToken = data?.token
      if (!sessionToken) {
        alert("Token not received. Please try again.")
        return
      }
      updateRecentGames(gameId)
      const lang = "en"
      const isMobile = window.innerWidth <= 768
      const mobileIndicator = isMobile ? "1" : ""
      const currentPageUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`
      const encodedCurrentPageUrl = encodeURIComponent(currentPageUrl)
      const launchURL = `${launchUrl}?s=${sessionToken}&l=${lang}&m=${mobileIndicator}&h=${encodedCurrentPageUrl}`

      // const launchURL = `${launchUrl}?s=${sessionToken}&l=${lang}&m=${mobileIndicator}`
      window.open(
        launchURL,
        "gameWindow",
        "width=1024,height=768,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes"
      )
    } catch (error) {
      console.error("Error launching game:", error)
      alert("An unexpected error occurred. Please try again.")
    }
  }

  useEffect(() => {
    if (window.innerWidth > 840) {
      document.body.style.overflow =
        filteredGames.length > 0 ? "hidden" : "auto"
    } else {
      document.body.style.overflow = "auto"
    }
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [filteredGames])

  const isProfilePage = location.pathname.includes("/profile")
  const hideOnProfileMobile = isProfilePage && windowWidth <= 840
  if (hideOnProfileMobile) {
    return null
  }

  return (
    <>
      <div className="searchpopup-games">
        {login && <LoginToPlay onClose={() => setLogin(false)} />}
        <div className="search-games-library-main">
          <div className="search-games-library-bar">
            <label>Looking for a game?</label>
            <div
              style={{
                position: "relative",
                display: "inline-block",
                width: "100%",
              }}
            >
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter game name or provider"
                style={{ width: "100%", paddingRight: "2rem" }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  clear
                </button>
              )}
            </div>
          </div>

          {searchQuery.trim() && (
            <div className="search-games-library-content">
              <div className="searchPopupResultsContainer">
                <div
                  className={`search-game-library-container ${
                    filteredGames.length
                      ? "search-game-library-filter-column"
                      : "search-game-library-filter"
                  }`}
                  // style={{
                  //   marginBottom: filteredGames.length ? "30px" : "30px",
                  //   marginTop: filteredGames.length ? "-47px" : "30px",
                  //   gridTemplateColumns: filteredGames.length
                  //     ? "repeat(2, 1fr)"
                  //     : "repeat(1, 1fr)",
                  // }}
                >
                  {filteredGames.length ? (
                    filteredGames.slice(0, searchResultsLimit).map((game) => (
                      <div key={game.gameId} className="gameWrapper">
                        <div className="game">
                          <GameImage gameId={game.gameId} />
                          <div className="gameDetails">
                            <div className="gameName">{game.gameName}</div>
                            <div className="gameProvider">
                              {game.provider || "Unknown"}
                            </div>
                            {game.placeholder ? (
                              <div className="game-loading-placeholder"></div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => launchGameInternal(game.gameId)}
                                className="fgh-play-now-btn"
                              >
                                Play Now
                              </button>
                            )}
                          </div>
                          <div className="game-overlay"></div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <>
                      <p>
                        We’re sorry, but we couldn’t find the game you’re
                        looking for.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="search-games-library-cta">
            {filteredGames.length > searchResultsLimit && (
              <a
                className="view-more-search-btn"
                onClick={() => setSearchResultsLimit((prev) => prev + 6)}
              >
                View More
              </a>
            )}
            <button className="btn-search-close" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default SearchGamesLibrary
